import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineEditCalendar,
  MdOutlinePayment,
  MdOutlineQrCode,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ANSET_SUBSCRIPTION_FORM_KEYS,
  PAYMENT_FREQUENCIES,
  PAYMENT_TYPES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { setDefaultIfEmpty } from "utils/helpers/input";

const Payment = ({
  setDisabled,
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("ANSET_SUBSCRIPTION", { returnObjects: true });

  const PAYMENT_TYPE = {
    [PAYMENT_TYPES.DEBIT]: () => {
      set(initialValues, "payment.frequency", PAYMENT_FREQUENCIES.MONTHLY);
      setDisabled((prev) => ({
        ...prev,
        payment: {
          ...prev.payment,
          frequency: false,
          debitDay: false,
          payer: {
            ...prev.payment?.payer,
            IBAN: false,
            BIC: false,
            owner: {
              ...prev.payment?.payer?.owner,
              firstName: false,
              lastName: false,
            },
          },
        },
        [ANSET_SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: false,
      }));
    },
    [PAYMENT_TYPES.DELEGATE]: () => {
      setDisabled((prev) => ({
        ...prev,
        payment: {
          ...prev.payment,
          frequency: true,
          debitDay: true,
          payer: {
            ...prev.payment?.payer,
            IBAN: true,
            BIC: true,
            owner: {
              ...prev.payment?.payer?.owner,
              firstName: true,
              lastName: true,
            },
          },
        },
        [ANSET_SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: true,
      }));
    },
    [PAYMENT_TYPES.TIP]: () => {},
  };

  const paymentTypePath = "payment.type";
  const paymentTypeField = bindField(paymentTypePath, {
    label: SUBSCRIPTION.FORM.PAYMENT_TYPE.LABEL,
    required: isFieldRequired(paymentTypePath),
    onChange: (value) => {
      PAYMENT_TYPE[value]?.();
    },
    onBlur: () => setPercent(initialValues),
  });

  const debitDayPath = "payment.debitDay";
  const debitDayField = bindField(debitDayPath, {
    label: SUBSCRIPTION.FORM.DEBIT_DATE,
    required: isFieldRequired(debitDayPath),
    onBlur: () => setPercent(initialValues),
  });

  const ibanPath = "payment.payer.IBAN";
  const ibanField = bindField(ibanPath, {
    label: SUBSCRIPTION.FORM.IBAN.LABEL,
    required: isFieldRequired(ibanPath),
    onBlur: () => setPercent(initialValues),
  });

  const bicPath = "payment.payer.BIC";
  const bicField = bindField(bicPath, {
    label: SUBSCRIPTION.FORM.BIC.LABEL,
    required: isFieldRequired(bicPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.SUBTITLE}
      >
        <div className="grid--1">
          <FormField
            path={paymentTypeField.path}
            label={paymentTypeField.label}
            required={paymentTypeField.required}
            disabled={paymentTypeField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlinePayment size={16} />}
                label={paymentTypeField.label}
                values={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.type"),
                  Object.values(PAYMENT_TYPES),
                ).map((value) => ({
                  value,
                  label: SUBSCRIPTION.FORM.PAYMENT_TYPE.OPTIONS[value],
                }))}
                value={get(initialValues, paymentTypePath)}
                {...field}
                onChange={paymentTypeField.onChange}
                onBlur={paymentTypeField.onBlur}
              />
            )}
          </FormField>
          {get(initialValues, "payment.type") === PAYMENT_TYPES.DEBIT && (
            <>
              <FormField
                path={debitDayField.path}
                label={debitDayField.label}
                required={debitDayField.required}
                disabled={debitDayField.disabled}
              >
                {(field) => (
                  <Select
                    options={["5", "10", "15", "25"].map((value) => ({
                      value,
                      label: value,
                    }))}
                    placeholder={SUBSCRIPTION.FORM.DEBIT_DATE}
                    label={debitDayField.label}
                    icon={<MdOutlineEditCalendar size={16} />}
                    defaultValue={debitDayField.defaultValue}
                    {...field}
                    onChange={debitDayField.onChange}
                    onBlur={debitDayField.onBlur}
                  />
                )}
              </FormField>
              <FormField
                path={ibanField.path}
                label={ibanField.label}
                required={ibanField.required}
                disabled={ibanField.disabled}
              >
                {(field) => (
                  <Input
                    label={ibanField.label}
                    icon={<MdOutlineQrCode size={16} />}
                    placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                    defaultValue={ibanField.defaultValue}
                    {...field}
                    onChange={ibanField.onChange}
                    onBlur={ibanField.onBlur}
                  />
                )}
              </FormField>
              <FormField
                path={bicField.path}
                label={bicField.label}
                required={bicField.required}
                disabled={bicField.disabled}
              >
                {(field) => (
                  <Input
                    label={bicField.label}
                    icon={<MdOutlineQrCode size={16} />}
                    placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                    defaultValue={bicField.defaultValue}
                    {...field}
                    onChange={bicField.onChange}
                    onBlur={bicField.onBlur}
                  />
                )}
              </FormField>
            </>
          )}
          {get(initialValues, "payment.type") === PAYMENT_TYPES.DELEGATE && (
            <div>
              <Alert
                type="info"
                showIcon
                message={SUBSCRIPTION.ADDITIONAL_INFORMATION.DELEGATE_PAYMENT}
              />
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};

export default Payment;
